import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { onStreamOpen } from '../servers/commandStream'
import { ApiError } from '../transport/http'
import { agentBackup, agentBackupStatus, type AgentRow } from '../transport/api'
import { getVault } from '../transport/relay'
import { VaultRecover, VaultReset, VaultSetup, VaultUnlock } from './VaultForms'
import { isOver, noteJob, useBackupJob } from './backupJobs'
import { jobFraction, jobLine, whyBackupFailed } from './backupWords'

/**
 * "Backup this agent" — owner, 2026-09-29: Agent 彈出來的視窗上面 … 加上一個 Backup this
 * agent 這個按鈕 … 需要有一個彈跳出來的視窗讓使用者可以輸入他的密碼.
 *
 * Opens under the card's Backup section, the way Fork opens under its row: the card is
 * already the one place an agent is administered, and a second sheet on top of it
 * would be a dialog about a dialog.
 *
 *   no vault yet     → set one up (the promises, the recovery code shown once) → back up
 *   a vault          → the password, checked on this device → back up
 *   forgot password  → the recovery code sets a new one, backups kept → back up
 *   lost both        → reset: every backup deleted, then a new vault
 *
 * The backup itself runs on the agent's MACHINE; this shows its progress from the
 * machine's own events (backupJobs.ts), with a slow status read as the floor.
 */
type Stage = 'vault' | 'setup' | 'unlock' | 'recover' | 'reset' | 'progress'

export function BackupForm(props: {
  agent: AgentRow
  deviceId: string
  machine: string
  onBack: () => void
}) {
  /*
   * IN VIEW when it opens and at every step. It opens where the button was, low on a
   * card that scrolls — and the owner has named this exact failure before (End's
   * confirmation below the fold on a phone: "the button does nothing").
   */
  const top = useRef<HTMLDivElement>(null)
  const [step, setStep] = useState('')
  useEffect(() => {
    top.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [step])
  return (
    <div ref={top} className="bk-wrap">
      <BackupSteps {...props} onStep={setStep} />
    </div>
  )
}

function BackupSteps({
  agent,
  deviceId,
  machine,
  onBack,
  onStep,
}: {
  agent: AgentRow
  deviceId: string
  machine: string
  onBack: () => void
  onStep: (s: string) => void
}): ReactNode {
  const qc = useQueryClient()
  const job = useBackupJob(deviceId, agent.session)
  const running = job !== null && !isOver(job)
  const [stage, setStage] = useState<Stage>(running ? 'progress' : 'vault')
  const [starting, setStarting] = useState(false)
  const [startErr, setStartErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const vault = useQuery({ queryKey: ['backup-vault'], queryFn: getVault, staleTime: 0, enabled: stage !== 'progress' })
  useEffect(() => onStep(stage), [stage, onStep])

  // Which door, once the vault is known.
  useEffect(() => {
    if (stage !== 'vault' || !vault.isSuccess) return
    setStage(vault.data.vault ? 'unlock' : 'setup')
  }, [stage, vault.isSuccess, vault.data])

  /*
   * THE FLOOR under the events — and no clock (the owner's standing rule: nothing
   * asks the same question on a timer). While a job runs, its machine is asked again
   * at the moments a lost event becomes knowable: the account stream reopening
   * (anything pushed while it was down is gone) and the app coming back into view.
   */
  const jobId = job?.job_id ?? null
  const status = useQuery({
    queryKey: ['backup-job', deviceId, jobId],
    queryFn: async () => {
      const r = await agentBackupStatus(deviceId, jobId!)
      if (r.job) noteJob(deviceId, r.job)
      return r.job ?? null
    },
    enabled: stage === 'progress' && running && !!jobId,
  })
  const { refetch } = status
  useEffect(() => {
    if (!(stage === 'progress' && running)) return
    const reask = () => void refetch()
    const offOpen = onStreamOpen(reask)
    const onVisible = () => {
      if (document.visibilityState === 'visible') reask()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      offOpen()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [stage, running, refetch])

  // A finished backup is news for the list in More.
  useEffect(() => {
    if (job?.phase === 'done') void qc.invalidateQueries({ queryKey: ['backups'] })
  }, [job?.phase, qc])

  const start = async () => {
    setStarting(true)
    setStartErr(null)
    /*
     * The jobs this machine already has, read BEFORE the press — so that if the press's
     * own reply is lost (below), "a job that was not there before" can only be this
     * press's. Without it a fresh page, which knows no jobs, would take an old finished
     * backup for a new one and say "Backed up" about a press that never arrived. A
     * failed read leaves it null, and then nothing is inferred.
     */
    const before = await agentBackupStatus(deviceId).then(
      (s) => new Set((s.jobs ?? []).map((j) => j.job_id)),
      () => null,
    )
    try {
      const r = await agentBackup(agent.session, deviceId)
      noteJob(deviceId, r.job)
      setStage('progress')
    } catch (e) {
      /*
       * "DID NOT ANSWER" IS NOT "DID NOT RUN". The reply to a relayed POST can be lost
       * on its way back — measured on dev 2026-09-29: 2 of ~38 presses, and both times
       * the machine had started AND finished the backup within a second while the card
       * waited 35 s and then said it failed. A backup is a job its machine can be asked
       * about, so ask (a read, which the relay retries) before saying anything.
       */
      if (before && e instanceof Error && /did not answer/.test(e.message)) {
        const began = await agentBackupStatus(deviceId).then(
          (s) => (s.jobs ?? []).find((j) => j.session === agent.session && !before.has(j.job_id)) ?? null,
          () => null,
        )
        if (began) {
          noteJob(deviceId, began)
          setStage('progress')
          return
        }
        setStartErr(`${machine} did not answer, and no new backup of ${agent.session} is there yet. It may still start — check More › Backups before trying again.`)
        return
      }
      const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | undefined) : undefined
      const code = e instanceof ApiError ? (body?.error ?? (e.status === 404 ? 'not_found' : null)) : null
      setStartErr(whyBackupFailed(code, body?.message ?? (e instanceof Error ? e.message : String(e)), { machine }))
    } finally {
      setStarting(false)
    }
  }

  if (stage === 'progress' && job) {
    const over = isOver(job)
    const failed = job.phase === 'failed'
    return (
      <div className="sm-fork bk-form" data-testid="backup-progress">
        <p className="bk-title">{failed ? 'Backup did not finish' : job.phase === 'done' ? 'Backed up' : 'Backing up…'}</p>
        {!failed && (
          <div
            className="bk-bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(jobFraction(job) * 100)}
          >
            <span style={{ width: `${Math.round(jobFraction(job) * 100)}%` }} />
          </div>
        )}
        <p className={`sm-hint${failed ? ' bk-bad' : ''}`} role={failed ? 'alert' : undefined}>
          {failed
            ? whyBackupFailed(job.error, job.message, { machine, accountBytes: vault.data?.policy.account_bytes })
            : jobLine(job, machine)}
        </p>
        {job.phase === 'done' && (
          <p className="sm-hint">
            See it under More → Backups.
            {job.pruned > 0 &&
              ` Removed ${job.pruned} older backup${job.pruned === 1 ? '' : 's'} of this agent — each agent keeps its newest ${vault.data?.policy.keep_per_agent ?? 5}.`}
          </p>
        )}
        {!over && <p className="sm-hint">You can close this card; the backup keeps going on {machine}.</p>}
        <div className="sm-actions">
          <button type="button" className="sheet-btn" onClick={onBack}>
            {over ? 'Done' : 'Hide'}
          </button>
          {failed && (
            <button type="button" className="sheet-btn primary" onClick={() => setStage('vault')}>
              Try again
            </button>
          )}
        </div>
      </div>
    )
  }

  if (stage === 'vault' || stage === 'progress') {
    return (
      <div className="sm-fork bk-form">
        {vault.isError ? (
          <>
            <p className="err-line" role="alert">
              {String((vault.error as Error)?.message ?? vault.error)}
            </p>
            <div className="sm-actions">
              <button type="button" className="sheet-btn" onClick={onBack}>
                Back
              </button>
            </div>
          </>
        ) : (
          <p className="sm-hint">Opening your backup vault…</p>
        )}
      </div>
    )
  }

  const startLine = startErr && (
    <p className="err-line" role="alert">
      {startErr}
    </p>
  )

  if (stage === 'setup') {
    return (
      <>
        {note && <p className="sm-hint bk-note">{note}</p>}
        <VaultSetup
          machine={machine}
          continueLabel={starting ? 'Starting…' : `Back up ${agent.session}`}
          onReady={() => void start()}
          onBack={onBack}
          onExists={() => {
            void qc.invalidateQueries({ queryKey: ['backup-vault'] })
            setStage('vault')
          }}
        />
        {startLine}
      </>
    )
  }

  if (stage === 'recover' && vault.data?.vault) {
    return (
      <VaultRecover
        vault={vault.data.vault}
        onBack={() => setStage('unlock')}
        onLostBoth={() => setStage('reset')}
        onDone={() => {
          void qc.invalidateQueries({ queryKey: ['backup-vault'] })
          setNote('Your new password is set. Your backups are kept.')
          void start()
        }}
      />
    )
  }

  if (stage === 'reset') {
    return (
      <VaultReset
        onBack={() => setStage('unlock')}
        onDone={(deleted) => {
          void qc.invalidateQueries({ queryKey: ['backup-vault'] })
          void qc.invalidateQueries({ queryKey: ['backups'] })
          setNote(`Vault reset — ${deleted} backup${deleted === 1 ? '' : 's'} deleted. Set up the new one.`)
          setStage('setup')
        }}
      />
    )
  }

  // unlock
  return (
    <>
      {note && <p className="sm-hint bk-note">{note}</p>}
      {vault.data?.vault && (
        <VaultUnlock
          vault={vault.data.vault}
          intro={`Enter your vault password to back up ${agent.session}. It is encrypted on ${machine} before it leaves; your password stays on this device.`}
          submitLabel="Back up"
          busyLabel={starting ? 'Starting…' : 'Checking…'}
          busy={starting}
          onUnlocked={() => void start()}
          onBack={onBack}
          onForgot={() => setStage('recover')}
        />
      )}
      {startLine}
    </>
  )
}
